'use client';

import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useOnClickOutsideWithIgnore } from '@services/useOnClickOutsideWithIgnore';
import { createPortal } from 'react-dom';
export interface LFModalProps {
  onClickOutside?: () => void;
  padding?: boolean;
  detached?: boolean;
  withOutsideClick?: boolean;
  bgColor?: string;
  fullScreen?: boolean;
  noScroll?: boolean;
  withBackdrop?: boolean;
  children: ReactNode;
  supportEsc?: boolean;
  allowClickThrough?: boolean;
  width?: string;
  usePortal?: boolean;
}
const LFModalBody: FC<LFModalProps> = observer(({
  children,
  width = 'w-full sm:w-2/3 md:w-1/2 lg:w-1/3 max-w-4xl',
  padding,
  withOutsideClick = true,
  detached = true,
  bgColor = 'bg-white',
  noScroll = false,
  fullScreen = false,
  withBackdrop = true,
  supportEsc = true,
  allowClickThrough = false,
  onClickOutside = () => null
}) => {
  const ref1 = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClickOutside();
      }
    };
    if (supportEsc) {
      document.addEventListener('keydown', handleKeyDown);
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
      if (supportEsc) {
        document.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [supportEsc, onClickOutside]);
  if (withOutsideClick) {
    useOnClickOutsideWithIgnore(ref1, onClickOutside);
  }
  return <div className={`
          fixed inset-0 z-9999 h-full w-full
          ${withBackdrop ? detached ? 'bg-black/50' : 'bg-black/85' : ''}
          overflow-y-auto duration-300
          ${allowClickThrough ? 'pointer-events-none bg-transparent' : ''}
        `} aria-labelledby="modal-title" role="dialog" aria-modal="true">
        {detached ? <div className={`
              flex h-full w-full items-center justify-center
              ${allowClickThrough ? 'pointer-events-auto' : ''}
            `}>
            <div className={`
                relative overflow-y-auto
                ${fullScreen ? 'w-full' : width}
                ${padding ? `
                      p-4
                      lg:p-11
                    ` : 'p-0'}
                z-30 mx-2 my-10 opacity-100
                sm:mx-auto
                ${bgColor}
                rounded-2xl
              `} ref={ref1}>
              {children}
            </div>
          </div> : <div className={`
              absolute right-0 overflow-y-auto
              ${allowClickThrough ? 'pointer-events-auto shadow-xl' : ''}
              ${noScroll ? 'overflow-y-hidden' : ''}
              h-full
              ${fullScreen ? 'w-full' : width}
              ${bgColor}
            `} ref={ref1}>
            {children}
          </div>}
      </div>;
});
export const LFModal: FC<LFModalProps> = observer(({
  usePortal = true,
  ...props
}) => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!usePortal) {
    return <LFModalBody {...props} />;
  }
  if (!mounted) return null;
  return createPortal(<LFModalBody {...props} />, document.body);
});